import React from 'react';
import { DataTable } from '../../components/ui/DataTable';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { StatusPill } from '../../components/ui/StatusPill';
import { AdminUser, deactivateAdminUser, getAdminUsers, reactivateAdminUser } from '../../services/system';

const PAGE_SIZE = 50;

export function AdminUsers({ searchQuery = '' }: { searchQuery?: string }) {
  const [admins, setAdmins] = React.useState<AdminUser[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [search, setSearch] = React.useState(searchQuery.trim());
  const [status, setStatus] = React.useState<'active' | 'inactive' | ''>('');
  const [deactivating, setDeactivating] = React.useState<AdminUser | null>(null);
  const [reason, setReason] = React.useState('');
  const [activeId, setActiveId] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');
    getAdminUsers({ limit: PAGE_SIZE, offset, search, status })
      .then((response) => {
        if (isActive) {
          setAdmins(response.admins);
          setTotal(response.total);
        }
      })
      .catch((requestError) => {
        if (isActive) setError(requestError instanceof Error ? requestError.message : 'Unable to load admin users');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => { isActive = false; };
  }, [offset, reloadKey, search, status]);

  const reactivate = async (admin: AdminUser) => {
    setActiveId(admin.id); setError(''); setMessage('');
    try {
      await reactivateAdminUser(admin.id);
      setMessage(`${admin.name} was reactivated and must sign in again.`);
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to reactivate admin user');
    } finally { setActiveId(''); }
  };

  const deactivate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!deactivating) return;
    setActiveId(deactivating.id); setError(''); setMessage('');
    try {
      await deactivateAdminUser(deactivating.id, reason.trim());
      setMessage(`${deactivating.name} was deactivated.`);
      setDeactivating(null); setReason('');
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to deactivate admin user');
    } finally { setActiveId(''); }
  };

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + admins.length, total);

  return (
    <div className="admin-invitations-page">
      <SectionHeader
        actions={(
          <div className="passenger-filters">
          <label>
            <span className="sr-only">Filter admin users by status</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value as 'active' | 'inactive' | ''); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
            <StatusPill tone="info">{admins.length} shown</StatusPill>
          </div>
        )}
        className="section-head passengers-head"
        subtitle="Deactivate and reactivate administrator user accounts."
        title="Admin users"
      />
      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}
      <DataTable className="admin-invitations-table">
          <thead><tr><th>Administrator</th><th>Username</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? <tr><td className="kyc-table-state" colSpan={5}>Loading admin users...</td></tr> : admins.length ? admins.map((admin) => (
              <tr key={admin.id}>
                <td data-label="Administrator"><strong>{admin.name}</strong><span className="admin-invitation-email">{admin.email}</span></td>
                <td data-label="Username">{admin.username}</td>
                <td data-label="Status"><StatusPill tone={admin.isActive ? 'success' : 'danger'}>{admin.isActive ? 'active' : 'deactivated'}</StatusPill></td>
                <td data-label="Created">{formatTime(admin.createdAt)}</td>
                <td data-label="Actions"><div className="admin-invitation-actions">{admin.isActive
                  ? <button className="btn btn-sm passenger-deactivate-btn" disabled={activeId === admin.id} type="button" onClick={() => setDeactivating(admin)}>Deactivate</button>
                  : <button className="btn btn-sm btn-primary" disabled={activeId === admin.id} type="button" onClick={() => reactivate(admin)}>{activeId === admin.id ? 'Reactivating...' : 'Reactivate'}</button>}
                </div></td>
              </tr>
            )) : <tr><td className="kyc-table-state" colSpan={5}>No admin users found.</td></tr>}
          </tbody>
      </DataTable>
      <div className="passengers-pagination"><span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span><div><button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button><button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button></div></div>
      {deactivating ? <div className="modal-backdrop" role="presentation" onMouseDown={() => setDeactivating(null)}>
        <form className="signup-dialog" role="dialog" aria-modal="true" aria-labelledby="deactivate-admin-title" onMouseDown={(event) => event.stopPropagation()} onSubmit={deactivate}>
          <div className="signup-dialog-head"><div><h2 id="deactivate-admin-title">Deactivate administrator</h2><span>{deactivating.name}</span></div><button className="btn-icon borderless" type="button" aria-label="Close deactivation form" onClick={() => setDeactivating(null)}>&times;</button></div>
          <label className="form-group"><span>Reason</span><textarea required placeholder="Access no longer required" value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          <div className="kyc-message">Current sessions will be revoked. A reactivated admin must sign in again.</div>
          <div className="kyc-dialog-actions"><button className="btn" type="button" onClick={() => setDeactivating(null)}>Cancel</button><button className="btn btn-danger" disabled={!reason.trim() || activeId === deactivating.id} type="submit">Deactivate admin</button></div>
        </form>
      </div> : null}
    </div>
  );
}

function formatTime(value: string) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Not provided';
}
